/**
 * PublicDailySummaryPage Component
 *
 * Publicly accessible page showing daily health summary in chronological order.
 * Includes images for each entry and weekly average weight progression chart.
 * Requires date and key parameters in the URL for access.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiUrl } from '../lib/api';

interface EntryImage {
  id: string;
  publicUrl: string;
  filename: string;
}

interface TimelineEntry {
  id: string;
  type: 'weight' | 'meal' | 'exercise' | 'health' | 'visit' | 'prescription';
  time: string | null;
  sortOrder: number;
  data: Record<string, any>;
  images: EntryImage[];
}

interface WeightDataPoint {
  date: string;
  weight: number;
  displayDate: string;
}

interface DailySummaryResponse {
  date: string;
  displayDate: string;
  timeline: TimelineEntry[];
  weightChart: WeightDataPoint[];
  summary: {
    totalEntries: number;
    weight: number;
    meals: number;
    exercise: number;
    health: number;
    visits: number;
    prescriptions: number;
  };
}

// Labels for meal types
const MEAL_TYPE_LABELS: Record<string, string> = {
  breakfast: 'Sarapan',
  snack1: 'Snack Pagi',
  lunch: 'Makan Siang',
  snack2: 'Snack Sore',
  dinner: 'Makan Malam',
};

// Labels for session types
const SESSION_TYPE_LABELS: Record<string, string> = {
  morning: 'Pagi',
  afternoon: 'Sore',
};

// Labels for metric types
const METRIC_TYPE_LABELS: Record<string, string> = {
  medication: 'Obat',
  vitamin: 'Vitamin',
  bowel: 'BAB',
  symptom: 'Gejala',
};

// Colors for entry types
const TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  weight: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  meal: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  exercise: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  health: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  visit: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  prescription: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
};

// Icons for entry types
const TYPE_ICONS: Record<string, string> = {
  weight: '⚖️',
  meal: '🍽️',
  exercise: '🏃',
  health: '💊',
  visit: '🏥',
  prescription: '📝',
};

/**
 * Simple line chart for weekly average weight progression
 */
function WeightChart({ data }: { data: WeightDataPoint[] }) {
  if (data.length === 0) {
    return null;
  }

  const weights = data.map(d => d.weight);
  const minWeight = Math.min(...weights) - 0.5;
  const maxWeight = Math.max(...weights) + 0.5;
  const range = maxWeight - minWeight || 1;

  const chartWidth = 320;
  const chartHeight = 160;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Generate points for the line
  const points = data.map((d, i) => {
    const x = padding.left + (i / (data.length - 1 || 1)) * innerWidth;
    const y = padding.top + innerHeight - ((d.weight - minWeight) / range) * innerHeight;
    return { x, y, ...d };
  });

  // Create SVG path for the line
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Create gradient area path
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + innerHeight} L ${points[0].x} ${padding.top + innerHeight} Z`;

  return (
    <div className="bg-white shadow rounded-lg p-4 mb-6">
      <h2 className="font-semibold text-gray-900 mb-1">Rata-rata Berat Mingguan</h2>
      <p className="text-xs text-gray-500 mb-3">Setiap titik adalah rata-rata dari berat harian dalam satu minggu.</p>
      <div className="overflow-x-auto">
        <svg width={chartWidth} height={chartHeight} className="mx-auto">
          {/* Gradient definition */}
          <defs>
            <linearGradient id="weightGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Y-axis labels */}
          {[0, 0.5, 1].map((ratio) => {
            const weight = minWeight + ratio * range;
            const y = padding.top + innerHeight - ratio * innerHeight;
            return (
              <g key={ratio}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerWidth}
                  y2={y}
                  stroke="#E5E7EB"
                  strokeDasharray="4"
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill="#6B7280"
                >
                  {weight.toFixed(2)}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          {points.length > 1 && (
            <path d={areaPath} fill="url(#weightGradient)" />
          )}

          {/* Line */}
          {points.length > 1 && (
            <path
              d={linePath}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Data points and labels */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4" fill="#3B82F6" />
              <circle cx={p.x} cy={p.y} r="2" fill="white" />
              {/* X-axis label */}
              <text
                x={p.x}
                y={padding.top + innerHeight + 15}
                textAnchor="middle"
                fontSize="9"
                fill="#6B7280"
              >
                {p.displayDate}
              </text>
              {/* Weight value on point */}
              <text
                x={p.x}
                y={p.y - 8}
                textAnchor="middle"
                fontSize="9"
                fontWeight="600"
                fill="#1F2937"
              >
                {p.weight.toFixed(2)}
              </text>
            </g>
          ))}
        </svg>
      </div>
      {/* Weight change summary */}
      {data.length >= 2 && (
        <div className="mt-3 text-center text-sm">
          <span className="text-gray-600">Perubahan rata-rata: </span>
          {(() => {
            const change = data[data.length - 1].weight - data[0].weight;
            const isLoss = change < 0;
            return (
              <span className={isLoss ? 'text-green-600 font-medium' : change > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                {change > 0 ? '+' : ''}{change.toFixed(2)} kg
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function formatWeight(value: string | number): string {
  const weight = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(weight)) {
    return typeof value === 'string' ? value : '0.00';
  }
  return weight.toFixed(2);
}

/**
 * Image gallery component for entry images
 */
function ImageGallery({ images }: { images: EntryImage[] }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2">
        {images.map((img, index) => (
          <button
            type="button"
            key={img.id}
            onClick={() => setLightboxIndex(index)}
            className="relative w-48 h-48 rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <img
              src={img.publicUrl}
              alt={img.filename}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((lightboxIndex - 1 + images.length) % images.length);
                }}
                className="absolute left-4 text-white hover:text-gray-300 p-2"
                aria-label="Previous"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((lightboxIndex + 1) % images.length);
                }}
                className="absolute right-4 text-white hover:text-gray-300 p-2"
                aria-label="Next"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          <img
            src={images[lightboxIndex].publicUrl}
            alt={images[lightboxIndex].filename}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 text-white text-sm">
              {lightboxIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function EntryCard({ entry }: { entry: TimelineEntry }) {
  const colors = TYPE_COLORS[entry.type];
  const icon = TYPE_ICONS[entry.type];
  const data = entry.data;

  const renderContent = () => {
    switch (entry.type) {
      case 'weight':
        return (
          <div>
            <div className="font-semibold text-lg">{formatWeight(data.weight as string | number)} kg</div>
            {data.notes && <p className="text-sm text-gray-600 mt-1">{data.notes as string}</p>}
          </div>
        );

      case 'meal':
        return (
          <div>
            <div className="font-semibold">{MEAL_TYPE_LABELS[data.mealType as string] || data.mealType}</div>
            <p className="text-sm mt-1">{data.description as string}</p>
            <div className="flex gap-4 mt-2 text-sm text-gray-600">
              {data.carbPortions && <span>Karbo: {data.carbPortions} porsi</span>}
              {data.proteinPortions && <span>Protein: {data.proteinPortions} porsi</span>}
            </div>
            {data.startTime && (
              <div className="text-xs text-gray-500 mt-1">
                {data.startTime as string}
                {data.endTime && ` - ${data.endTime as string}`}
              </div>
            )}
            {data.notes && <p className="text-sm text-gray-600 mt-1 italic">{data.notes as string}</p>}
          </div>
        );

      case 'exercise':
        return (
          <div>
            <div className="font-semibold">
              {SESSION_TYPE_LABELS[data.sessionType as string] || data.sessionType} - {data.exerciseType as string}
            </div>
            <div className="text-sm text-gray-600 mt-1">Durasi: {data.durationMinutes} menit</div>
            {data.notes && <p className="text-sm text-gray-600 mt-1 italic">{data.notes as string}</p>}
          </div>
        );

      case 'health':
        return (
          <div>
            <div className="font-semibold">{METRIC_TYPE_LABELS[data.metricType as string] || data.metricType}</div>
            {data.description && <p className="text-sm mt-1">{data.description as string}</p>}
            {data.hasBowelMovement && (
              <div className="text-sm text-gray-600 mt-1">
                BAB: {data.hasBowelMovement === 'yes' ? 'Ya' : 'Tidak'}
              </div>
            )}
            {data.notes && <p className="text-sm text-gray-600 mt-1 italic">{data.notes as string}</p>}
          </div>
        );

      case 'visit':
        return (
          <div>
            <div className="font-semibold">Dr. {data.doctorName as string}</div>
            {data.specialty && <div className="text-sm text-gray-600">{data.specialty as string}</div>}
            {data.hospitalClinic && <div className="text-sm text-gray-600">{data.hospitalClinic as string}</div>}
            {data.diagnosis && (
              <div className="mt-2">
                <span className="text-sm font-medium">Diagnosis: </span>
                <span className="text-sm">{data.diagnosis as string}</span>
              </div>
            )}
            {data.notes && <p className="text-sm text-gray-600 mt-1 italic">{data.notes as string}</p>}
          </div>
        );

      case 'prescription':
        return (
          <div>
            <div className="font-semibold">{data.medicationName as string}</div>
            <div className="flex gap-4 mt-1 text-sm text-gray-600">
              {data.dosage && <span>Dosis: {data.dosage as string}</span>}
              {data.frequency && <span>Frekuensi: {data.frequency as string}</span>}
            </div>
            {data.duration && <div className="text-sm text-gray-600">Durasi: {data.duration as string}</div>}
            {data.notes && <p className="text-sm text-gray-600 mt-1 italic">{data.notes as string}</p>}
          </div>
        );

      default:
        return <div>Unknown entry type</div>;
    }
  };

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
              {entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}
            </span>
            {data.time && (
              <span className="text-xs text-gray-500">{data.time as string}</span>
            )}
          </div>
          {renderContent()}
          {/* Images */}
          <ImageGallery images={entry.images} />
        </div>
      </div>
    </div>
  );
}

export function PublicDailySummaryPage() {
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');
  const key = searchParams.get('key');

  const [data, setData] = useState<DailySummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!date || !key) {
      setError('Parameter tanggal dan kunci diperlukan');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        apiUrl(`/api/public/daily-summary?date=${encodeURIComponent(date)}&key=${encodeURIComponent(key)}`)
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          setError('Kunci akses tidak valid');
        } else if (response.status === 400) {
          setError(errorData.error || 'Parameter tidak valid');
        } else {
          setError('Gagal memuat data');
        }
        setData(null);
      } else {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
      setError('Gagal memuat data. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [date, key]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Missing parameters view
  if (!date || !key) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white shadow rounded-lg p-6 max-w-md w-full text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Akses Diperlukan</h1>
          <p className="text-gray-600">
            Halaman ini memerlukan parameter <code className="bg-gray-100 px-1 rounded">date</code> dan{' '}
            <code className="bg-gray-100 px-1 rounded">key</code> di URL.
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Contoh: ?date=2024-01-15&key=your-api-key
          </div>
        </div>
      </div>
    );
  }

  // Loading view
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Error view
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white shadow rounded-lg p-6 max-w-md w-full text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
          <button
            type="button"
            onClick={fetchSummary}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  // No data
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white shadow rounded-lg p-6 max-w-md w-full text-center">
          <div className="text-4xl mb-4">💭</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Data Tidak Ditemukan</h1>
          <p className="text-gray-600">Tidak ada data untuk tanggal yang diminta.</p>
        </div>
      </div>
    );
  }

  // Separate weight entries from other timeline entries
  const weightEntries = data.timeline.filter(entry => entry.type === 'weight');
  const otherEntries = data.timeline.filter(entry => entry.type !== 'weight');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h1 className="text-xl font-bold text-gray-900">Ringkasan Harian</h1>
          <p className="text-gray-600 mt-1">{data.displayDate}</p>
        </div>

        {/* Weight Section - Chart and Entries at Top */}
        {(weightEntries.length > 0 || (data.weightChart && data.weightChart.length > 0)) && (
          <div className="mb-6">
            {/* Weight Chart */}
            {data.weightChart && data.weightChart.length > 0 && (
              <WeightChart data={data.weightChart} />
            )}

            {/* Today's Weight Entries */}
            {weightEntries.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-sm text-gray-500 font-medium">Berat Hari Ini</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                {weightEntries.map((entry) => (
                  <EntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
          {data.summary.weight > 0 && (
            <div className="bg-white shadow rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-600">{data.summary.weight}</div>
              <div className="text-xs text-gray-500">Berat</div>
            </div>
          )}
          {data.summary.meals > 0 && (
            <div className="bg-white shadow rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-orange-600">{data.summary.meals}</div>
              <div className="text-xs text-gray-500">Makan</div>
            </div>
          )}
          {data.summary.exercise > 0 && (
            <div className="bg-white shadow rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-600">{data.summary.exercise}</div>
              <div className="text-xs text-gray-500">Olahraga</div>
            </div>
          )}
          {data.summary.health > 0 && (
            <div className="bg-white shadow rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-purple-600">{data.summary.health}</div>
              <div className="text-xs text-gray-500">Kesehatan</div>
            </div>
          )}
          {data.summary.visits > 0 && (
            <div className="bg-white shadow rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-red-600">{data.summary.visits}</div>
              <div className="text-xs text-gray-500">Kunjungan</div>
            </div>
          )}
          {data.summary.prescriptions > 0 && (
            <div className="bg-white shadow rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-pink-600">{data.summary.prescriptions}</div>
              <div className="text-xs text-gray-500">Resep</div>
            </div>
          )}
        </div>

        {/* Other Timeline Entries (excluding weight) */}
        {otherEntries.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-sm text-gray-500">Pagi sampai Malam</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            {otherEntries.map((entry) => (
              <EntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        ) : (
          data.timeline.length === 0 && (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <div className="text-4xl mb-4">💭</div>
              <p className="text-gray-600">Tidak ada catatan untuk tanggal ini</p>
            </div>
          )
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-400">
          Mami Health Tracker
        </div>
      </div>
    </div>
  );
}
